import { useEditorStore } from "@/store";
import { InfoBanner } from "@/components/layout/InfoBanner";
import { SectionHeader } from "@/components/fields/SectionHeader";
import { ColorField } from "@/components/fields/ColorField";
import { NumberField } from "@/components/fields/NumberField";
import { SelectField } from "@/components/fields/SelectField";

export function EnvironmentSection() {
  const style = useEditorStore((s) => s.style);
  const updateRoot = useEditorStore((s) => s.updateRoot);

  const light = (style.light ?? {}) as Record<string, unknown>;
  const terrain = (style.terrain ?? {}) as Record<string, unknown>;
  const sky = (style.sky ?? {}) as Record<string, unknown>;
  const projection = (style.projection ?? {}) as Record<string, unknown>;
  const transition = (style.transition ?? {}) as Record<string, unknown>;

  const setLight = (key: string, val: unknown) => {
    updateRoot("light", { ...light, [key]: val });
  };
  const setTerrain = (key: string, val: unknown) => {
    updateRoot("terrain", { ...terrain, [key]: val });
  };
  const setSky = (key: string, val: unknown) => {
    updateRoot("sky", { ...sky, [key]: val });
  };

  // Get raster-dem source IDs for terrain source picker
  const sources = (style.sources ?? {}) as Record<string, Record<string, unknown>>;
  const demSources = Object.entries(sources)
    .filter(([, s]) => s.type === "raster-dem")
    .map(([id]) => ({ value: id, label: id }));

  return (
    <div>
      <InfoBanner storageKey="section-environment">
        Global settings that affect the entire map. Light controls 3D building
        shading. Terrain adds elevation from a DEM source. Transition sets
        how fast property changes animate. These only take effect when relevant
        layers or features are present.
      </InfoBanner>

      {/* Light */}
      <SectionHeader title="Light" description="3D lighting for extruded geometry">
        <SelectField
          label="Anchor"
          value={light.anchor as string}
          onChange={(v) => setLight("anchor", v)}
          options={[
            { value: "viewport", label: "Viewport" },
            { value: "map", label: "Map" },
          ]}
        />
        <ColorField
          label="Color"
          value={(light.color as string) ?? "#ffffff"}
          onChange={(v) => setLight("color", v)}
        />
        <NumberField
          label="Intensity"
          value={(light.intensity as number) ?? 0.5}
          onChange={(v) => setLight("intensity", v)}
          min={0}
          max={1}
          step={0.05}
        />
      </SectionHeader>

      {/* Terrain */}
      <SectionHeader title="Terrain" description="3D terrain visualization">
        {demSources.length > 0 ? (
          <>
            <SelectField
              label="DEM Source"
              value={terrain.source as string}
              onChange={(v) => setTerrain("source", v)}
              options={[{ value: "", label: "None" }, ...demSources]}
            />
            <NumberField
              label="Exaggeration"
              value={(terrain.exaggeration as number) ?? 1}
              onChange={(v) => setTerrain("exaggeration", v)}
              min={0}
              max={10}
              step={0.1}
            />
          </>
        ) : (
          <p className="text-xs text-text-muted">
            Add a raster-dem source to enable terrain.
          </p>
        )}
      </SectionHeader>

      {/* Sky (experimental) */}
      <SectionHeader title="Sky" description="Atmospheric sky rendering (experimental)">
        <ColorField
          label="Sky Color"
          value={(sky["sky-color"] as string) ?? "#88C6FC"}
          onChange={(v) => setSky("sky-color", v)}
        />
        <ColorField
          label="Horizon Color"
          value={(sky["horizon-color"] as string) ?? "#ffffff"}
          onChange={(v) => setSky("horizon-color", v)}
        />
        <ColorField
          label="Fog Color"
          value={(sky["fog-color"] as string) ?? "#ffffff"}
          onChange={(v) => setSky("fog-color", v)}
        />
        <NumberField
          label="Fog Ground Blend"
          value={(sky["fog-ground-blend"] as number) ?? 0.5}
          onChange={(v) => setSky("fog-ground-blend", v)}
          min={0} max={1} step={0.05}
        />
        <NumberField
          label="Atmosphere Blend"
          value={(sky["atmosphere-blend"] as number) ?? 0.8}
          onChange={(v) => setSky("atmosphere-blend", v)}
          min={0} max={1} step={0.05}
        />
      </SectionHeader>

      {/* Projection */}
      <SectionHeader title="Projection">
        <SelectField
          label="Type"
          value={(projection.type as string) ?? "mercator"}
          onChange={(v) => updateRoot("projection", { ...projection, type: v })}
          options={[
            { value: "mercator", label: "Mercator" },
            { value: "vertical-perspective", label: "Vertical Perspective" },
          ]}
        />
      </SectionHeader>

      {/* Transition */}
      <SectionHeader title="Transition" description="Global animation defaults">
        <NumberField
          label="Duration (ms)"
          value={(transition.duration as number) ?? 300}
          onChange={(v) => updateRoot("transition", { ...transition, duration: v })}
          min={0}
          max={5000}
          step={50}
        />
        <NumberField
          label="Delay (ms)"
          value={(transition.delay as number) ?? 0}
          onChange={(v) => updateRoot("transition", { ...transition, delay: v })}
          min={0}
          max={5000}
          step={50}
        />
      </SectionHeader>
    </div>
  );
}

